import React, { memo, useCallback, useMemo, useState } from 'react';
import { View } from 'react-native';
import { useNavigation } from '@react-navigation/native';

import { MY_DAY_STEP_STATUS, docketRequiresMoneyBagReturn } from '../../../../common/utils/myDay/driverDaySteps';
import { ActionDock, EmptyState, Screen, SectionTitle, TickRow } from '../../../design/components';
import { useThemedStyles } from '../../../hooks/useThemedStyles';
import { useDriverDay } from '../DriverDayProvider';
import DriverTrainingBanner from '../DriverTrainingBanner';
import { RUN_LOAD_ERROR } from '../driverRoutes';
import useDriverDayActions from '../useDriverDayActions';
import StepCount from './StepCount';
import StepNote from './StepNote';
import { countLabel, getStopLabels } from './stopLabels';
import useEndOfDaySteps from './useEndOfDaySteps';

const STEP_KEY = 'moneyBags';

const MoneyBagRow = memo(({ docket, checked, disabled, onToggle }) => {
	const handleToggle = useCallback(nextChecked => onToggle(docket.id, nextChecked), [docket.id, onToggle]);
	const { site, position, machineNumber } = getStopLabels(docket);

	return (
		<TickRow
			title={site || position || 'Unnamed stop'}
			subtitle={[
				site ? position : null,
				machineNumber,
				docket.cashBag ? `Bag ${docket.cashBag}` : 'Collect cash',
			].filter(Boolean).join(' · ')}
			checked={checked}
			disabled={disabled}
			onToggle={handleToggle}
		/>
	);
});

/**
 * Money bags return (End of day): one tick per machine that collected cash;
 * the confirmation unlocks once every bag is ticked. Ticks are a counting aid
 * on this screen only; the confirmation is the existing My day step (offline
 * queue as before).
 */
const MoneyBagsView = () => {
	const navigation = useNavigation();
	const styles = useThemedStyles(createStyles);
	const day = useDriverDay();
	const goBack = useCallback(() => navigation.goBack(), [navigation]);
	const { confirmStepByKey, isConfirmingStep } = useDriverDayActions(day, { onStepConfirmed: goBack });
	const { steps, getStepAfter, getStepPosition } = useEndOfDaySteps(day);
	const [tickedById, setTickedById] = useState({});

	const step = steps.find(item => item.key === STEP_KEY) || null;
	const isDone = step?.status === MY_DAY_STEP_STATUS.DONE;
	const isBlocked = step?.status === MY_DAY_STEP_STATUS.BLOCKED;
	const dockets = useMemo(() => day.sortedDockets.filter(docketRequiresMoneyBagReturn), [day.sortedDockets]);
	const tickedCount = isDone ? dockets.length : dockets.filter(docket => tickedById[docket.id]).length;
	const allTicked = dockets.length > 0 && tickedCount === dockets.length;
	const bagsLabel = countLabel(dockets.length, 'bag', 'bags');

	const toggle = useCallback((docketId, nextChecked) => {
		setTickedById(previous => ({ ...previous, [docketId]: nextChecked }));
	}, []);
	const confirm = useCallback(() => confirmStepByKey(STEP_KEY), [confirmStepByKey]);

	const renderDock = () => {
		if (!step || isBlocked) {
			return null;
		}
		if (isDone) {
			return <ActionDock action={{ label: 'Back to end of day', onPress: goBack }} />;
		}

		const stepAfter = getStepAfter(STEP_KEY);
		let hint = stepAfter ? `After this: ${stepAfter.label}` : undefined;
		if (!allTicked) {
			hint = `Tick all ${bagsLabel} to continue`;
		}
		else if (!day.isOnline && !day.isTraining) {
			hint = 'You are offline. This is saved on the phone and syncs later.';
		}

		return (
			<ActionDock
				hint={hint}
				action={{
					label: `Confirm ${bagsLabel} returned`,
					onPress: confirm,
					loading: isConfirmingStep,
					disabled: !allTicked,
					icon: allTicked ? undefined : 'lock',
				}}
			/>
		);
	};

	return (
		<Screen
			backLabel="End of day"
			headerRight={<StepCount position={getStepPosition(STEP_KEY)} />}
			heading="Money bags return"
			subheading="Count your bags, put them on the return pile and tick each one off."
			isLoading={day.isLoading}
			error={day.loadError ? RUN_LOAD_ERROR : undefined}
			onRetry={day.refresh}
			banner={<DriverTrainingBanner />}
			dock={renderDock()}
		>
			{
				step && dockets.length ? (
					<>
						{isDone && <StepNote tone="success" text={step.description} />}
						{isBlocked && <StepNote tone="warning" text={step.description} />}
						<SectionTitle title={`${bagsLabel} today`} meta={`${tickedCount} of ${dockets.length} ticked`} />
						<View style={styles.list}>
							{dockets.map(docket => (
								<MoneyBagRow
									key={docket.id}
									docket={docket}
									checked={isDone || Boolean(tickedById[docket.id])}
									disabled={isDone || isBlocked}
									onToggle={toggle}
								/>
							))}
						</View>
					</>
				) : (
					<EmptyState title="No money bags today" message="None of today's machines collected cash." />
				)
			}
		</Screen>
	);
};

const createStyles = ({ ui: { space } }) => ({
	list: {
		gap: space.sm,
	},
});

export default MoneyBagsView;
